import React from "react";
import { Link } from "react-router-dom";
import { MathBlock, MathInline } from "../components/MathRender";
import { useAuth } from "../context/AuthContext";
import { BookOpen, Sigma, ShieldCheck, Scale, Trophy, Users, Microscope, ArrowRight } from "lucide-react";

const MODULES = [
  { icon: BookOpen, title: "Learn", desc: "Structured content, exercises, and referenced explanations with first-class math typography.", to: "/learn" },
  { icon: Sigma, title: "Solve", desc: "Commissioned problems, qualification, versioned submissions, and contracted awards.", to: "/solve" },
  { icon: ShieldCheck, title: "Verify", desc: "Independent review with distinct labels: computational check, expert review, formal proof.", to: "/verify" },
  { icon: Scale, title: "Tribunal", desc: "Evidence, conflict-free adjudication, appeals, and immutable decision records.", to: "/tribunal" },
  { icon: Microscope, title: "Research", desc: "Sponsored investigations, private collaboration, and explicit IP terms.", to: "/solve" },
  { icon: Trophy, title: "Arena", desc: "Competitions with published eligibility, judging, tie, and prize rules.", to: "/solve" },
];

const JOURNEY = [
  "Customer drafts work",
  "Terms & review budget approved",
  "Funding confirmed",
  "Qualified contributors submit",
  "Independent review",
  "Acceptance or Tribunal",
  "Delivery & payout",
];

const DEFAULTS = [
  { h: "Money", p: "Marketplace escrow with a visible earnings ledger. Reviewer work is priced separately. Unused award funding is refunded under disclosed terms; completed review work stays payable." },
  { h: "Awards", p: "Acceptance criteria, deadlines, award count, tie rules, fees, and IP are fixed before funding. Paid assigned work is preferred; speculative contests are clearly labelled." },
  { h: "Disputes", p: "Two independent reviews for paid acceptance. Disagreements route to a conflict-free adjudicator. Reviewers earn fixed compensation unrelated to their verdict." },
  { h: "Integrity & rights", p: "Disclosed AI assistance permitted unless forbidden. Detectors give leads, not verdicts. Submissions stay private; ownership is agreed before work starts." },
];

export default function Landing() {
  const { user } = useAuth();
  const hero = "https://images.unsplash.com/photo-1776520771782-a0ce13b95a3e?crop=entropy&cs=srgb&fm=jpg&q=85";

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={hero} alt="" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FFFFFF]/60 via-[#FFFFFF]/85 to-[#FFFFFF]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 pb-24 pt-20 sm:px-8 sm:pt-28">
          <p className="eyebrow mb-5" data-testid="hero-eyebrow">Learn · Solve · Verify · Adjudicate</p>
          <h1 className="max-w-4xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            A rigorous marketplace for mathematics that pays, proves, and protects.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Axioms by Mahakali Studios unites a learning institute, a commission marketplace, and a formal
            verification engine — with fair compensation, two-reviewer acceptance, and a conflict-free Tribunal.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to={user ? "/solve" : "/register"} data-testid="hero-cta-primary" className="btn-crimson">
              {user ? "Enter the marketplace" : "Create your account"} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/learn" data-testid="hero-cta-learn" className="btn-ghost">Explore the library</Link>
          </div>

          <div className="mt-12 max-w-xl">
            <p className="eyebrow mb-2">Typeset with KaTeX</p>
            <MathBlock>{"\\int_a^b f(x)\\,dx = F(b) - F(a) \\qquad a \\equiv b \\pmod{n}"}</MathBlock>
            <p className="mt-3 text-sm text-slate-500">
              Every lesson, statement, and submission renders live LaTeX — inline like{" "}
              <span className="text-[#B45309]"><MathInline>{"e^{i\\pi}+1=0"}</MathInline></span> or as display blocks.
            </p>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="mb-10 flex items-end justify-between">
          <div>
            <p className="eyebrow mb-2">The complete scope</p>
            <h2 className="font-display text-2xl tracking-tight text-slate-900 sm:text-4xl">Seven connected workspaces</h2>
          </div>
          <Users className="hidden h-10 w-10 text-[#CBD5E1] sm:block" />
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {MODULES.map((m) => (
            <Link to={m.to} key={m.title} data-testid={`module-card-${m.title.toLowerCase()}`} className="axiom-card group p-6">
              <m.icon className="h-7 w-7 text-[#E61A27]" />
              <h3 className="mt-4 font-display text-xl text-slate-900">{m.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{m.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-xs text-[#D4AF37] opacity-0 transition-opacity group-hover:opacity-100">
                Open <ArrowRight className="h-3 w-3" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* JOURNEY */}
      <section className="border-y border-[#CBD5E1] bg-[#FFF9F0]">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <p className="eyebrow mb-2">The core journey</p>
          <h2 className="font-display text-2xl tracking-tight text-slate-900 sm:text-3xl">From draft to protected payout</h2>
          <div className="mt-8 flex flex-wrap gap-3">
            {JOURNEY.map((step, i) => (
              <div key={step} className="flex items-center gap-3">
                <div className="axiom-card flex items-center gap-3 px-4 py-3" data-testid={`journey-step-${i}`}>
                  <span className="font-mono text-xs text-[#E61A27]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm text-slate-700">{step}</span>
                </div>
                {i < JOURNEY.length - 1 && <ArrowRight className="hidden h-4 w-4 text-[#CBD5E1] lg:block" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEFAULTS / GOVERNANCE */}
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <p className="eyebrow mb-2">Proposed defaults & edge-case rules</p>
        <h2 className="font-display text-2xl tracking-tight text-slate-900 sm:text-3xl">Governance you can test, not just read</h2>
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
          {DEFAULTS.map((d) => (
            <div key={d.h} className="axiom-card p-6" data-testid={`default-card-${d.h.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <h3 className="font-display text-lg text-[#B45309]">{d.h}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{d.p}</p>
            </div>
          ))}
        </div>
        <div className="axiom-card mt-6 border-[#E61A27]/30 bg-[#FEF2F2] p-5" data-testid="live-money-notice">
          <p className="text-sm text-slate-600">
            <span className="font-mono text-xs uppercase tracking-wider text-[#E61A27]">Live-money disabled</span> — the
            earnings ledger runs on a simulated escrow. Real payments stay off until launch country, legal entity,
            provider approval, and reserve funding are approved.
          </p>
        </div>
      </section>

      <footer className="border-t border-[#CBD5E1] py-8">
        <div className="mx-auto max-w-7xl px-5 text-center text-xs text-slate-500 sm:px-8">
          Axioms by Mahakali Studios · Built for correctness, fair compensation, and reliable dispute handling.
        </div>
      </footer>
    </div>
  );
}
